import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { formatMoney, SETTINGS_REGISTRY, type SettingDef } from '@johnsaks/core'
import { useEffect, useMemo, useState } from 'react'
import { BtnPrimary, Field, TextArea, TextInput } from '@/components/field'
import { useToast } from '@/components/toast'
import { api, apiMutate } from '@/lib/api'

type SettingsMap = Record<string, unknown>

const SECTION_LABELS: Record<string, string> = {
  boutique: 'Boutique',
  reseaux: 'Réseaux sociaux',
  paiements: 'Paiements',
  commandes: 'Commandes',
  stock: 'Stock',
  discretion: 'Discrétion',
}

function asBool(v: unknown, fallback = false) {
  if (typeof v === 'boolean') return v
  if (v === 'true' || v === 1) return true
  if (v === 'false' || v === 0) return false
  return fallback
}

function asStr(v: unknown, fallback = '') {
  return typeof v === 'string' ? v : fallback
}

function asNum(v: unknown, fallback = 0) {
  return typeof v === 'number' && Number.isFinite(v) ? v : fallback
}

function groupBySection(defs: readonly SettingDef[]) {
  const map = new Map<string, SettingDef[]>()
  for (const d of defs) {
    const list = map.get(d.section) ?? []
    list.push(d)
    map.set(d.section, list)
  }
  return [...map.entries()]
}

export function Reglages() {
  const toast = useToast()
  const qc = useQueryClient()
  const settings = useQuery({
    queryKey: ['admin-settings'],
    queryFn: () => api<SettingsMap>('/admin/settings'),
  })

  const sections = useMemo(() => groupBySection(SETTINGS_REGISTRY), [])
  const [activeSection, setActiveSection] = useState(sections[0]?.[0] ?? 'boutique')
  const [draft, setDraft] = useState<SettingsMap>({})
  const [dirty, setDirty] = useState(false)

  useEffect(() => {
    if (!settings.data) return
    const next: SettingsMap = {}
    for (const def of SETTINGS_REGISTRY) {
      const raw = settings.data[def.key]
      next[def.key] = raw === undefined ? def.default : raw
    }
    setDraft(next)
    setDirty(false)
  }, [settings.data])

  useEffect(() => {
    function onBeforeUnload(e: BeforeUnloadEvent) {
      if (!dirty) return
      e.preventDefault()
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', onBeforeUnload)
    return () => window.removeEventListener('beforeunload', onBeforeUnload)
  }, [dirty])

  function setValue(key: string, value: unknown) {
    setDraft((d) => ({ ...d, [key]: value }))
    setDirty(true)
  }

  const save = useMutation({
    mutationFn: () => {
      const defs = SETTINGS_REGISTRY.filter((d) => d.section === activeSection)
      const body: SettingsMap = {}
      for (const d of defs) body[d.key] = draft[d.key]
      return apiMutate('/admin/settings', 'PATCH', body)
    },
    onSuccess: () => {
      toast.ok('Section enregistrée')
      setDirty(false)
      void qc.invalidateQueries({ queryKey: ['admin-settings'] })
    },
    onError: (e: Error) => toast.err(e.message),
  })

  if (settings.isLoading) {
    return <p className="text-second-admin">Chargement…</p>
  }

  const activeDefs = sections.find(([id]) => id === activeSection)?.[1] ?? []

  return (
    <div className="grid gap-5 lg:grid-cols-[14rem_1fr]">
      <nav className="carte-admin flex flex-col gap-1 p-3">
        <p className="kicker px-2 pb-2 text-second-admin">Sommaire</p>
        {sections.map(([id]) => (
          <button
            key={id}
            type="button"
            onClick={() => {
              if (dirty && !window.confirm('Modifications non enregistrées. Continuer ?')) return
              setActiveSection(id)
              setDirty(false)
              if (settings.data) {
                const next: SettingsMap = {}
                for (const def of SETTINGS_REGISTRY) {
                  const raw = settings.data[def.key]
                  next[def.key] = raw === undefined ? def.default : raw
                }
                setDraft(next)
              }
            }}
            className={`rounded-pilule px-3 py-2 text-left text-sm ${
              activeSection === id
                ? 'bg-encre-admin text-creme'
                : 'text-encre-admin hover:bg-fond-admin'
            }`}
          >
            {SECTION_LABELS[id] ?? id}
          </button>
        ))}
      </nav>

      <section className="carte-admin grid max-w-xl gap-4 p-5">
        <div className="flex items-center justify-between gap-3">
          <h2 className="text-base">{SECTION_LABELS[activeSection] ?? activeSection}</h2>
          {dirty ? <span className="kicker text-ambre">Non enregistré</span> : null}
        </div>

        {activeDefs.map((def) => (
          <Field key={def.key} label={def.label}>
            {def.type === 'boolean' ? (
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={asBool(draft[def.key], Boolean(def.default))}
                  onChange={(e) => setValue(def.key, e.target.checked)}
                />
                Activer
              </label>
            ) : def.type === 'int' || def.type === 'cents' ? (
              <TextInput
                type="number"
                value={String(asNum(draft[def.key], Number(def.default) || 0))}
                onChange={(e) => setValue(def.key, Number(e.target.value))}
              />
            ) : def.key === 'shop.announcement' ? (
              <TextArea
                rows={2}
                value={asStr(draft[def.key])}
                onChange={(e) => setValue(def.key, e.target.value)}
              />
            ) : def.type === 'enum' ? (
              <select
                className="w-full rounded-md border border-bordure-admin bg-white px-3 py-2 text-sm"
                value={asStr(draft[def.key], String(def.default))}
                onChange={(e) => setValue(def.key, e.target.value)}
              >
                {(def.enumValues ?? []).map((v) => (
                  <option key={v} value={v}>
                    {v}
                  </option>
                ))}
              </select>
            ) : (
              <TextInput
                value={asStr(draft[def.key], String(def.default ?? ''))}
                onChange={(e) => setValue(def.key, e.target.value)}
              />
            )}
            {def.type === 'cents' ? (
              <p className="mt-1 text-xs text-second-admin">
                {formatMoney(asNum(draft[def.key], 0))}
              </p>
            ) : null}
            {def.description ? (
              <p className="mt-1 text-xs text-second-admin">{def.description}</p>
            ) : null}
          </Field>
        ))}

        <BtnPrimary disabled={save.isPending || !dirty} onClick={() => save.mutate()}>
          Enregistrer cette section
        </BtnPrimary>
      </section>
    </div>
  )
}
